<template>
  <div class="dashboard-root">
    <div class="dashboard-header">
      <h2 class="dashboard-title">All Jobs</h2>
      <p class="dashboard-subtitle">Overview of all scheduled automations</p>
    </div>

    <div class="filters-wrapper">
      <div class="filters-container">
        <div class="search-box">
          <i class="bi bi-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search jobs..."
            class="filter-input"
          />
        </div>

        <div class="filters-group">
          <select v-model="selectedPostType" class="filter-select">
            <option value="">All Types</option>
            <option value="Reel">Reel</option>
            <option value="Photo">Photo</option>
          </select>

          <div class="status-filters">
            <label
              v-for="status in statusOptions"
              :key="status"
              class="status-checkbox"
            >
              <input
                type="checkbox"
                :value="status"
                v-model="selectedStatuses"
              />
              <span class="checkbox-label">{{ status }}</span>
            </label>
          </div>
          <b-button
            variant="primary"
            class="new-job-button"
            @click="$router.push('/new-automation')"
          >
            Create New Job
          </b-button>
        </div>
      </div>
    </div>

    <template v-if="loading">
      <div class="loading-container">
        <b-spinner style="width: 3rem; height: 3rem" type="grow"></b-spinner>
        <p class="loading-text">Loading...</p>
      </div>
    </template>

    <div v-else class="table-wrapper">
      <b-table
        @row-clicked="onRowClicked"
        :fields="fields"
        :items="filteredItems"
        class="data-table"
        responsive
        small
      >
        <template #cell(name)="data">
          <RouterLink :to="'/automation-detail/' + data.item._id">
            <span class="job-name" :class="getStatusClass(data.value)">
              {{ data.value }}
            </span>
          </RouterLink>
        </template>
        <template #cell(status)="data">
          <span class="status-badge" :class="getStatusClass(data.value)">
            {{ data.value }}
          </span>
        </template>
        <template #cell(postType)="data">
          <b-icon
            icon="camera-reels-fill"
            scale="1"
            variant="danger"
            v-if="data.item.postType === 'Reel'"
          >
          </b-icon>
          <b-icon icon="camera-fill" variant="primary" v-else> </b-icon>
        </template>
        <template #cell(interval)="data">
          <span>
            {{ data.item.intervalMin }} - {{ data.item.intervalMax }} min
          </span>
        </template>
        <template #cell(postsCount)="data">
          <span>
            {{ data.item.postsCount }}
          </span>
        </template>
        <template #cell(createdAt)="data">
          <span>
            {{ data.item.createdAt }}
          </span>
        </template>
      </b-table>
    </div>
  </div>
</template>

<style scoped>
.new-job-button {
  margin-left: auto;
  background-color: #fff;
  color: #0077ff;
  font-weight: 700;
}
/* Dashboard Root */
.dashboard-root {
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
  background: #f8f8f8;
  border-radius: 5px;
  padding: 25px;
}

/* Header */
.dashboard-header {
  margin-bottom: 2rem;
  text-align: left;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #343a40;
  margin-bottom: 0.5rem;
}

.dashboard-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-text {
  color: #6c757d;
  margin-top: 1rem;
}

/* Table Wrapper */
.table-wrapper {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

/* Table */
.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border-radius: 5px;
}

.data-table th {
  color: #495057;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.data-table tbody tr:hover {
  background-color: #b8b8b8 !important;
  cursor: pointer !important;
}

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 0.4rem 0.7rem;
  border-radius: 1rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.status-done {
  background-color: #d1fae5;
  color: #1a7f37;
}

.status-running {
  background-color: #e3f0fd;
  color: #2563eb;
}

.status-paused {
  background-color: #fff4e6;
  color: #ff8800;
}

.status-terminated {
  background-color: #fde8e8;
  color: #b91c1c;
}

.status-error {
  background-color: #fbeaea;
  color: #c81e1e;
}

/* Filters Wrapper */
.filters-wrapper {
  background: #0077ff;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 119, 255, 0.2);
}

/* Filters Container */
.filters-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  opacity: 0.7;
}

.filter-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.filter-input:focus {
  border-color: #0077ff;
  outline: none;
}

.filters-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.filter-select {
  padding: 0.75rem 2rem 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  min-width: 150px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}

.filter-select option {
  color: #333;
}

.status-filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.status-checkbox {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.status-checkbox input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  background: transparent;
}

.status-checkbox input[type="checkbox"]:checked {
  background: white;
  border-color: white;
}

.status-checkbox input[type="checkbox"]:checked::after {
  content: "";
  width: 6px;
  height: 12px;
  border: solid #0077ff;
  border-width: 0 2px 2px 0;
  position: absolute;
  top: 1px;
  left: 5px;
  transform: rotate(45deg);
}

.checkbox-label {
  font-size: 0.95rem;
  color: white;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-root {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.7rem;
  }

  .table-wrapper {
    padding: 1rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem;
  }

  .filters-container {
    gap: 1rem;
  }

  .filters-group {
    flex-direction: column;
    align-items: stretch;
  }

  .status-filters {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}
</style>

<script>
import router from "@/router";
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      loading: true,
      items: [],
      searchQuery: "",
      selectedPostType: "",
      selectedStatuses: ["Running"],
      statusOptions: ["Running", "Paused", "Terminated", "Completed", "Error"],
      postTypeOptions: ["Reels", "Pages"],
      fields: [
        { key: "name", label: "Job Name" },
        { key: "pageName", label: "Page" },
        { key: "postType", label: "Type" },
        { key: "interval", label: "Interval" },
        { key: "postsCount", label: "Posts" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Created" },
      ],
    };
  },
  computed: {
    filteredItems() {
      return this.items.filter((item) => {
        const searchMatch = item.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());
        const statusMatch =
          this.selectedStatuses.length === 0 ||
          this.selectedStatuses.includes(item.status);
        const typeMatch =
          !this.selectedPostType || item.postType === this.selectedPostType;
        return searchMatch && statusMatch && typeMatch;
      });
    },
  },
  methods: {
    onRowClicked(item) {
      router.push("/automation-detail/" + item._id);
    },
    getStatusClass(status) {
      return {
        "status-done": status === "Completed" || status === "Done",
        "status-running": status === "Running",
        "status-paused": status === "Paused",
        "status-terminated": status === "Terminated",
        "status-error": status === "Error",
      };
    },
  },
  async mounted() {
    try {
      const response = await axios.get("/api/automations/all");
      const _itemsAll = response.data.data;
      const mappedItems = _itemsAll.map((item) => ({
        _id: item._id,
        name: item.name,
        pageName: item.pageName,
        postType: item.postType,
        intervalMin: item.intervalMin,
        intervalMax: item.intervalMax,
        interval: `${item.intervalMin} - ${item.intervalMax} min`,
        postsCount: Array.isArray(item.posts) ? item.posts.length : 0,
        status: item.status,
        createdAt:
          moment(item.createdAt).format("hh:mm A | DD-MM-YYYY") + " | UK",
      }));
      this.items = mappedItems;
    } catch {
      this.$toast.error("Failed to load data");
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style>
thead tr {
  height: 50px;
  vertical-align: middle;
  font-size: 19px;
}
thead tr th {
  background: #0077ff !important;
  color: #fff !important;
}

thead tr th:first-child {
  padding-left: 15px;
}

.data-table tbody tr:hover {
  background: #0077ff !important;
  cursor: pointer !important;
}

tbody tr td:first-child {
  padding-left: 15px;
}

.job-name {
  color: #0077ff;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
</style>
